import { useState } from 'react';

import { Alert } from '../../components/ui/alert.tsx';
import { Button } from '../../components/ui/button.tsx';
import { Card } from '../../components/ui/card.tsx';
import { Field, Select } from '../../components/ui/field.tsx';
import { Loading } from '../../components/ui/skeleton.tsx';
import {
  keys,
  useApiMutation,
  useSecuritySettings,
  type SecuritySettings,
} from '../../lib/admin-api.ts';
import { api, errorMessage } from '../../lib/api.ts';
import { useToast } from '../../lib/toast.tsx';

export function SecurityPage() {
  const settings = useSecuritySettings();
  const notify = useToast();
  const [confirmOff, setConfirmOff] = useState(false);
  const save = useApiMutation(
    (mfaRequired: boolean) =>
      api<SecuritySettings>('/settings/security', {
        method: 'PATCH',
        body: { mfaRequired },
      }),
    [keys.security],
  );
  const change = (value: boolean) => {
    save.mutate(value, {
      onSuccess: () => {
        setConfirmOff(false);
        notify(
          value
            ? 'La verificación en dos pasos ahora es obligatoria.'
            : 'La verificación en dos pasos ahora es opcional.',
          'success',
        );
      },
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Seguridad</h1>
        <p className="text-sm text-muted-foreground">Reglas de acceso para toda la inmobiliaria.</p>
      </div>
      {settings.isPending ? (
        <Loading />
      ) : settings.error ? (
        <Alert tone="danger">{errorMessage(settings.error)}</Alert>
      ) : (
        <Card className="space-y-4">
          <div>
            <h2 className="font-semibold">Verificación en dos pasos obligatoria</h2>
            <p className="text-sm text-muted-foreground">
              {settings.data.mfaRequired
                ? 'Activada: todas las personas deben usar una aplicación de autenticación además de su contraseña.'
                : 'Desactivada: cada persona decide si la usa. Quien ya la activó la seguirá usando.'}
            </p>
          </div>
          {save.error && <Alert tone="danger">{errorMessage(save.error)}</Alert>}
          {settings.data.mfaRequired ? (
            confirmOff ? (
              <Alert tone="warning" title="¿Seguro que quieres desactivarla?">
                <p>
                  Sin ella, una contraseña robada basta para entrar y ver datos de clientes y
                  propietarios.
                </p>
                <div className="mt-3 flex gap-2">
                  <Button
                    variant="danger"
                    loading={save.isPending}
                    onClick={() => {
                      change(false);
                    }}
                  >
                    Desactivar
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setConfirmOff(false);
                    }}
                  >
                    Cancelar
                  </Button>
                </div>
              </Alert>
            ) : (
              <Button
                variant="secondary"
                onClick={() => {
                  setConfirmOff(true);
                }}
              >
                Hacerla opcional
              </Button>
            )
          ) : (
            <Button
              variant="brand"
              loading={save.isPending}
              onClick={() => {
                change(true);
              }}
            >
              Hacerla obligatoria
            </Button>
          )}
        </Card>
      )}
      {settings.data && <TrustedDevicesSetting days={settings.data.trustedDeviceDays} />}
    </div>
  );
}

/** "Recordar este dispositivo": 14 or 30 days, or off (forgets every remembered browser). */
function TrustedDevicesSetting({ days }: { days: 14 | 30 | null }) {
  const notify = useToast();
  const save = useApiMutation(
    (trustedDeviceDays: 14 | 30 | null) =>
      api<SecuritySettings>('/settings/security', {
        method: 'PATCH',
        body: { trustedDeviceDays },
      }),
    [keys.security],
  );

  return (
    <Card className="space-y-4">
      <div>
        <h2 className="font-semibold">Recordar dispositivo</h2>
        <p className="text-sm text-muted-foreground">
          Al escribir el código de verificación, cada persona puede marcar «Recordar este
          dispositivo»: ese navegador no le pedirá el código durante el plazo elegido (sí la
          contraseña). Deja de valer al cambiar la contraseña, al reiniciar su verificación o al
          suspenderla, y cada quien puede olvidarlo en Mi cuenta.
        </p>
      </div>
      {save.error && <Alert tone="danger">{errorMessage(save.error)}</Alert>}
      <Field
        label="Plazo"
        hint={
          days === null
            ? 'Desactivado: siempre se pide el código.'
            : 'Si lo desactivas, todos los dispositivos recordados vuelven a pedir el código.'
        }
      >
        {(a11y) => (
          <Select
            {...a11y}
            className="sm:w-64"
            value={days === null ? 'off' : String(days)}
            disabled={save.isPending}
            onChange={(event) => {
              const value =
                event.target.value === 'off' ? null : event.target.value === '14' ? 14 : 30;
              save.mutate(value, {
                onSuccess: () => {
                  notify(
                    value === null
                      ? 'Desactivado: los dispositivos recordados vuelven a pedir el código.'
                      : `Los dispositivos se recuerdan durante ${String(value)} días.`,
                    'success',
                  );
                },
              });
            }}
          >
            <option value="30">30 días</option>
            <option value="14">14 días</option>
            <option value="off">Desactivado</option>
          </Select>
        )}
      </Field>
    </Card>
  );
}
